import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@/components/ui';
import { getReferralOverride, setReferralOverride } from '@/data/client';
import { apiErrMsg } from './util';

/* The referral credit this account earns per qualified referral. Blank means
   the global default from Admin → Referral Rewards. Fixed on each referral at
   the moment it is marked qualified, so a change never rewrites past credits. */
export function ReferralCreditForm({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'referral-override', orgId], queryFn: () => getReferralOverride(orgId) });
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { if (data) setAmount(data.override == null ? '' : String(data.override)); }, [data]);
  const dirty = !!data && (amount.trim() === '' ? data.override != null : Number(amount) !== data.override);
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await setReferralOverride(orgId, amount.trim() === '' ? null : Number(amount));
      setMsg(amount.trim() === '' ? `Using the global default, $${data?.defaultAmount ?? 100}.` : `Saved: $${Number(amount)} per qualified referral.`);
      qc.invalidateQueries({ queryKey: ['admin', 'referral-override', orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    } catch (e) { setMsg(apiErrMsg(e, 'Could not save.')); }
    finally { setBusy(false); }
  };
  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">Referral credit</h4>
      <div className="admin-prof">
        <Input label="Credit per qualified referral ($)" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={data ? `Default $${data.defaultAmount}` : ''}
          hint={`Blank uses the global default ($${data?.defaultAmount ?? 100}, set under Admin → Referral Rewards). Applies to referrals marked qualified from now on; earlier credits keep their amount.`} />
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button size="sm" variant="outline" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save credit'}</Button>
          {data && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>This account currently earns ${data.rate} per qualified referral.</span>}
          {msg && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{msg}</span>}
        </div>
      </div>
    </section>
  );
}
